import { useLoadAddressMutation } from "@features/addressSelector/addressSelectorApi";
import { Tabs } from "@mantine/core";
import { Fragment, useEffect, useRef, useState } from "react";
import { PiSpinnerBold } from "react-icons/pi";
import {
  RiArrowRightSLine,
  RiCheckboxCircleFill,
  RiCloseLine,
  RiSearchLine,
} from "react-icons/ri";
import "./style.css";
import InputWrapper from "@components/Input";

const stateKey = "state";
const districtKey = "district";
const vdcKey = "vdc_municipality";

const AddressSelector = ({
  addressValue,
  handleSelectLocations,
  fullWidth,
  selectorWidthClass,
  errors,
  disabled,
}) => {
  const [currentAddress, setCurrentAddress] = useState("state");
  const [open, setOpen] = useState(false);
  const [filterTxt, setFilterTxt] = useState("");
  const [addressArray, setAddressArray] = useState([]);
  const [addressApi, setAddressApi] = useState("state");

  const addressRef = useRef();

  // rtk;
  const [loadAddress, { isLoading: loading }] = useLoadAddressMutation();

  useEffect(() => {
    if (open) {
      loadAddress(addressApi)
        .unwrap()
        .then((res) => {
          if (res?.success) {
            switch (currentAddress) {
              case stateKey:
                setAddressArray(res?.data);
                break;
              case districtKey:
                setAddressArray(res?.data);
                break;
              case vdcKey:
                setAddressArray(res?.data);
                break;
            }
          }
        });
    }
  }, [addressApi, open]);

  const isSelectedAddress = (address_id) => {
    if (addressValue?.[currentAddress]?._id === address_id) {
      return true;
    }
    return false;
  };

  const isTabDisabled = (address_type) => {
    switch (address_type) {
      case stateKey:
        return false;
      case districtKey:
      case vdcKey:
        if (addressValue?.[address_type]?._id) {
          return false;
        }
        return true;
      default:
        return false;
    }
  };

  const handleClearFilterTxt = () => {
    setFilterTxt("");
  };

  const handleSelectAddressType = (address_type) => {
    switch (address_type) {
      case stateKey:
        setAddressApi(`state`);
        handleClearFilterTxt();
        break;
      case districtKey:
        setAddressApi(`state/${addressValue?.state?._id}`);
        handleClearFilterTxt();
        break;
      case vdcKey:
        setAddressApi(`district/${addressValue?.district?._id}`);
        handleClearFilterTxt();
        break;
    }
    setCurrentAddress(address_type);
  };

  const handleSetAddress = (address) => {
    handleSelectLocations({
      key: currentAddress,
      _id: address._id,
      name: address.name,
    });
    handleClearFilterTxt();

    switch (currentAddress) {
      case stateKey:
        setAddressApi(`state/${address._id}`);
        setCurrentAddress("district");
        break;
      case districtKey:
        setAddressApi(`district/${address._id}`);
        setCurrentAddress("vdc_municipality");
        break;
      case vdcKey:
        setOpen(false);
        break;
    }
  };

  const handleChangeFilterTxt = (e) => {
    setFilterTxt(e.target.value);
  };

  const handleOpenAddress = (address_id, address_type, currentAddress) => {
    if (address_id && address_type) {
      setAddressApi(`${address_type}/${address_id}`);
    } else {
      setAddressApi(address_type);
    }
    setCurrentAddress(currentAddress);
    setOpen(true);
  };

  const hasErrors = () => {
    if (errors[stateKey] || errors[districtKey] || errors[vdcKey]) {
      return true;
    }
    return false;
  };

  const returnAddressDiv = (each) => {
    return (
      <div
        className='flex cursor-pointer hover:bg-gray-200 justify-between items-center px-4 py-2 duration-150'
        key={each._id}
        onClick={() => handleSetAddress(each)}
      >
        <div>{each.name}</div>
        {isSelectedAddress(each._id) && (
          <>
            <RiCheckboxCircleFill size='20' className='text-primary' />
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutSide = (event) => {
      if (
        open &&
        addressRef.current &&
        !addressRef.current.contains(event.target)
      ) {
        if (open) {
          setOpen(false);
          setCurrentAddress("state");
          // setAddressApi("state");
        }
      }
    };
    document.addEventListener("click", handleClickOutSide, true);
    return () =>
      document.removeEventListener("click", handleClickOutSide, true);
  }, [addressRef, open]);

  return (
    <div
      className={`relative ${!fullWidth ? "w-1/2" : "w-full"} mb-4`}
      ref={addressRef}
    >
      <div>
        <label className='font-medium mb-px text-xs'>
          Location
          <span className='text-danger ml-px'>*</span>
        </label>
        <div
          className={`flex items-center gap-1.5 rounded-md border px-3 py-[6px] text-sm ${
            hasErrors() ? "border-danger" : "border-gray-300"
          }`}
        >
          <button
            disabled={disabled}
            className='disabled:opacity-60 disabled:cursor-not-allowed'
            onClick={() => handleOpenAddress(null, stateKey, stateKey)}
          >
            {addressValue?.state?.name || "Select State"}
          </button>
          <button
            disabled={addressValue?.state?._id ? false : true}
            className='disabled:opacity-60 disabled:cursor-not-allowed'
          >
            <RiArrowRightSLine size={20} />
          </button>
          <button
            onClick={() =>
              handleOpenAddress(addressValue?.state?._id, stateKey, districtKey)
            }
            disabled={addressValue?.state?._id ? false : true}
            className='disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {addressValue?.district?.name || "Select District"}
          </button>
          <button
            disabled={addressValue?.state?._id ? false : true}
            className='disabled:opacity-60 disabled:cursor-not-allowed'
          >
            <RiArrowRightSLine size={20} />
          </button>
          <button
            onClick={() =>
              handleOpenAddress(
                addressValue?.district?._id,
                districtKey,
                vdcKey
              )
            }
            disabled={addressValue?.district?._id ? false : true}
            className='disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {addressValue?.vdc_municipality?.name || "Select Area"}
          </button>
        </div>
        {hasErrors() && (
          <div className='text-xs mt-1 text-danger'>
            Some location is missing. Please select all locations.
          </div>
        )}
      </div>
      {open && (
        <div
          className={`absolute address-slidedown top-[12] ${
            selectorWidthClass || "w-full"
          } bg-white shadow-md text-sm z-20`}
        >
          <Tabs value={currentAddress} defaultValue={addressTypes[0].value}>
            <Tabs.List grow>
              {addressTypes.map((tab) => (
                <Tabs.Tab
                  key={tab.value}
                  value={tab.value}
                  onClick={() => handleSelectAddressType(tab.value)}
                  disabled={isTabDisabled(tab.value)}
                >
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            <InputWrapper
              placeholder='Search Address...'
              value={filterTxt}
              onChange={handleChangeFilterTxt}
              rightSection={<RiSearchLine />}
              divclassname='w-full p-2'
            />
            {loading ? (
              <div className='text-center p-4 h-56 flex gap-2 justify-center items-center'>
                <PiSpinnerBold className='animate-spin' size='20' /> Loading
                Address...
              </div>
            ) : (
              <div className='my-2 h-56 overflow-y-auto'>
                {addressArray?.length > 0 ? (
                  <>
                    {filterTxt === "" ? (
                      <>
                        {addressArray.map((each) => (
                          <Fragment key={each._id}>
                            {returnAddressDiv(each)}
                          </Fragment>
                        ))}
                      </>
                    ) : (
                      addressArray
                        .filter((x) =>
                          x.name.toLowerCase().includes(filterTxt.toLowerCase())
                        )
                        .map((each) => (
                          <Fragment key={each._id}>
                            {returnAddressDiv(each)}
                          </Fragment>
                        ))
                    )}
                  </>
                ) : (
                  <div className='flex justify-center gap-1 h-full items-center'>
                    <RiCloseLine size='20' /> Address Not Found.
                  </div>
                )}
              </div>
            )}
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default AddressSelector;

const addressTypes = [
  {
    label: "State",
    value: "state",
  },
  {
    label: "District",
    value: "district",
  },
  {
    label: "Area",
    value: "vdc_municipality",
  },
];
